// cleanSlate itemsList — leaf extension of the base itemsList component JS.
//
// The theme's first leaf component module: ESM (Vite), importing the base CommonJS module
// it extends. scripts/index.js globs cleanSlate's components/*/*.js last, so this module
// replaces base's for the 'itemsList' type.
//
// It only adds what cleanSlate's own itemsList.tpl / table.tpl need on top of base:
// - the selection bar (count + "with selected items" pills), shown once a row is ticked;
// - the quick (primary) filters row, toggled by the toolbar magnifier;
// - the actions column rendered last: table.tpl puts its heading last, and each JS-built
//   row's actions cell is moved to the end after base displayItems().
// Everything else — loading, filters, drawers (base toggleAside), select-all, sorting — is
// base behaviour, untouched.

import baseItemsList from '../../../../../core/themes/base/components/itemsList/itemsList.js';

const itemsList = Object.assign({}, baseItemsList, {
    // Object.assign replaces a nested map wholesale, so extend base's subelements instead
    // of redeclaring them by hand. The new ones default to null, so hasSubelement() reports
    // them absent when the template does not render them (no selected actions / no primary
    // filters).
    subelements: Object.assign({}, baseItemsList.subelements, {
        selectionBar: null,
        selectionCount: null,
        quickFilters: null,
        quickFiltersToggler: null
    }),

    init: function () {
        baseItemsList.init.call(this);

        // Magnifier toggles the quick filters row; aria-expanded mirrors its visibility.
        if (this.hasSubelement('quickFiltersToggler') && this.hasSubelement('quickFilters')) {
            this.subelements.quickFiltersToggler.addEventListener('click', (e) => {
                e.preventDefault();
                this.hm.helper.toggleVisibilityElement(this.subelements.quickFilters);
                let expanded = !this.hm.helper.isHidden(this.subelements.quickFilters);
                this.subelements.quickFiltersToggler.setAttribute('aria-expanded', expanded ? 'true' : 'false');
            });
        }

        // Any change inside the table (a row tick, or select-all propagating to the rows)
        // recomputes the selection bar. Base's own table listener keeps the master
        // checkbox's checked / indeterminate state.
        if (this.hasSubelement('selectionBar')) {
            this.subelements.table.addEventListener('change', () => {
                this.updateSelection();
            });
        }
    },

    // Show the selection bar with "<n> selected" while at least one row is ticked; hide it
    // otherwise. Rows are resolved the way base initSelectAll does: the per-row checkbox
    // components tagged with this list's id in their selectorFor extra.
    updateSelection: function () {
        if (!this.hasSubelement('selectionBar')) {
            return;
        }

        let rowCheckboxes = this.hm.app.componentsWithExtra('selectorFor', this.id);
        let checkedCount = 0;

        rowCheckboxes.forEach((rowCheckbox) => {
            if (rowCheckbox.element.checked) {
                checkedCount++;
            }
        });

        if (this.hasSubelement('selectionCount')) {
            this.subelements.selectionCount.textContent = checkedCount + ' ' + this.hm.app.text('itemsSelected');
        }

        this.hm.helper.showElement(this.subelements.selectionBar, checkedCount > 0);
    },

    // A reload drops every row (and its checkbox component), so the selection is gone:
    // reset the master checkbox and hide the bar after base has cleared the list.
    clearList: function () {
        baseItemsList.clearList.call(this);

        if (this.hasSubelement('selectAllCheckbox')) {
            this.subelements.selectAllCheckbox.checked = false;
            this.subelements.selectAllCheckbox.indeterminate = false;
        }

        this.updateSelection();
    },

    // Base builds each row as [sort handle][row checkbox][actions][columns…]; move the
    // actions cell to the end so it lines up with table.tpl's last-column heading. Moving
    // a node keeps its already-initialised components working (references survive the
    // move). The empty-state row carries no item id, so it is skipped.
    displayItems: function (response) {
        baseItemsList.displayItems.call(this, response);

        if (!this.hasActions.items || response.items.length === 0) {
            return;
        }

        let actionsCellIndex = (this.enableSort ? 1 : 0) + (this.hasActions.selected ? 1 : 0);
        let rows = this.subelements.table.querySelectorAll('tbody tr[data-zit-item-id]');

        rows.forEach((row) => {
            let actionsCell = row.cells[actionsCellIndex];

            if (typeof actionsCell !== typeof undefined) {
                row.appendChild(actionsCell);
            }
        });
    }
});

export default itemsList;
